import React, { useState, useMemo } from 'react';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import {
  Typography,
  Divider,
  Paper,
  Tooltip,
  IconButton,
  SvgIcon,
  CircularProgress
} from '@material-ui/core';
import {
  AuxInfo,
  BadgeSection,
  PriceSection,
  ViewContainersModal
 } from 'components/underwrite/shared/header';
import {
  HeaderImage,
  ViewCrmModal,
  ReloadStatusModal
} from 'components/underwrite/acquisition/propertyUnderwrite/header';
import { fullAddress } from 'utilities/address';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  setChannelWorkflowGridStateUpdate, fetchMsrHomes, setIsFetchingMsrHomes, fetchOpportunityHistory,
  setSelectedOpportunityHistoryTab
} from 'store/actions';
import { push } from 'connected-react-router';
import { Constants } from 'utilities/constants';
import HouseIcon from '@material-ui/icons/House';
import FilterDramaIcon from '@material-ui/icons/FilterDrama';
import ViewOpportunityHistoryModal from 'components/underwrite/acquisition/propertyUnderwrite/header/ViewOpportunityHistoryModal';
import {
  MsrHomesModal,
  UnderwriteWarningBanner
} from 'components/underwrite/shared';
import AmherstIcon from "assets/images/rex/AmherstIcon.png";
import WaveIcon from '@material-ui/icons/Waves';
import PropertyUWDiscrepantData from './PropertyUWDiscrepantData';
import WarningIcon from '@material-ui/icons/Warning'; 
import { useSaveUWDialog } from 'components/underwrite/shared/SaveUWDialog';

const excludedReasons = ['Below % of List Threshold']; // Exclude no bid reason when value is Below % of List 
 
const hasNoBidReason = opp => { 
  return opp.noBidReason != null && !excludedReasons.includes(opp.noBidReason); 
} 
 
const hasDoNotBuyWarning = opp => { 
  return opp.crmTerminationReason === 'Do Not Buy'; 
} 
 
const hasHighFidelityFieldsWarning = (opp, currentOppId) => { 
  if (opp.id === currentOppId) return false; 
  //return opp.crmProposedRepairBidAmount != null || opp.sellerHoa != null || opp.sellerTaxes != null; 
  return opp.crmProposedRepairBidAmount != null || opp.sellerHoa != null; //EF 20250721 temp removal of seller taxes
}

const PropertyUWHeader = ({ containerClass, classes, displayMode, push, selectedOpportunity,
  inspectionOpen, setChannelWorkflowGridStateUpdate, valuationIsDirty,
  fetchMsrHomes, setIsFetchingMsrHomes, fetchOpportunityHistory,
  isAmherstManaged, selectedContainer, selectedOpportunityReloadStatusIsLoading, selectedOpportunityReloadStatusError, selectedOpportunityReloadStatusTopLevelStatus,
  opportunitySfhaDetails, isOpportunitySfhaDetailLoading, opportunities, setSelectedOpportunityHistoryTab
}) => {
  const [viewCrmModalIsOpen, setViewCrmModalIsOpen] = useState(false);
  const [isMsrHomesModalOpen, setIsMsrHomesModalOpen] = useState(false);
  const [reloadStatusModalIsOpen, setReloadStatusModalIsOpen] = useState(false);
  const { openSaveUWDialog } = useSaveUWDialog();

  const titleColorClass = useMemo(() => { 
    if (!opportunities) return null;
    if (opportunities.some(opp => hasDoNotBuyWarning(opp))) { // RED (Do Not Buy) takes absolute precedence 
      return classes.errorButtonColor; 
    } else if (opportunities.some(opp => hasNoBidReason(opp))) { // if not RED, check YELLOW (No Bid Reason) 
      return classes.warningButtonColor; 
    } else if (opportunities.some(opp => hasHighFidelityFieldsWarning(opp, selectedOpportunity?.id))) { // if neither RED nor YELLOW, check BLUE (High Fidelity) 
      return classes.blueButtonColor; 
    } else return null; 
  }, [opportunities, selectedOpportunity?.id]);

  const reloadStatusColorClass = useMemo(() => {
    if (selectedOpportunityReloadStatusError) {
      return classes.errorButtonColor;
    }
    if (!selectedOpportunityReloadStatusTopLevelStatus) return null;    
    if (selectedOpportunityReloadStatusTopLevelStatus === 'Error') {
      return classes.errorButtonColor;
    } else if (selectedOpportunityReloadStatusTopLevelStatus === 'Stale') {
      return classes.warningButtonColor;
    } else {
      return null;
    }
  }, [selectedOpportunityReloadStatusTopLevelStatus]);

  if (!selectedOpportunity) return null;

  if (!selectedContainer) return null;

  const inspectionClick = (e) => {
    inspectionOpen(e);
  }

  const handleViewCrmInfo = () => {
    setViewCrmModalIsOpen(true);
  }

  const handleViewOpportunityHistory = () => {
    fetchOpportunityHistory({ txnOppId: selectedOpportunity.id });
  }

  const handleViewReloadStatus = () => {
    setReloadStatusModalIsOpen(true);
  }

  const viewOppOnChannelWorkflow = id => {
    if (valuationIsDirty) {
      openSaveUWDialog({ 
        saveUWDialogParameters: { route: Constants.Routes.CHANNEL_WORKFLOW },
        callback: () => {
          setChannelWorkflowGridStateUpdate({ filterValue: [['id', '=', id]] }, true);
        }
      });
    } else {
      setChannelWorkflowGridStateUpdate({ filterValue: [['id', '=', id]] }, true); // Filter grid by selectedOpportunity id
      push(Constants.Routes.CHANNEL_WORKFLOW);
    }
  }

  const hasInspection = selectedOpportunity?.inspectionFileUrl || selectedOpportunity?.inspectionJsonResponse;
  const hasCrmRelationship = selectedOpportunity?.isCrmOriginated || selectedOpportunity?.hadSuccessfulCrmPush; // Check if has relationship with SF

  const getHistoryTooltip = () => { 
    const hasHistoricalNoBidReason = opportunities.some(opp => hasNoBidReason(opp));
    const hasHistoricalDoNotBuyWarning = opportunities.some(opp => hasDoNotBuyWarning(opp));
    const hasHistoricalHighFidelityFieldsWarning = opportunities.some(opp => hasHighFidelityFieldsWarning(opp, selectedOpportunity?.id));
    let toolTipText = ''; 
   
    // 1) Build up any existing yellow/red warnings: 
    if (hasHistoricalNoBidReason) { 
      toolTipText = ' - No Bid Reason warning'; 
    } 
   
    if (hasHistoricalDoNotBuyWarning) { 
      const dnbWarning = 'Do Not Buy warning'; 
      if (toolTipText === '') { 
        toolTipText = ` - ${dnbWarning}`; 
      } else { 
        // remove the word “warning” from any prior item, then add a comma before Do Not Buy 
        toolTipText = `${toolTipText.replace(' warning', '')}, ${dnbWarning}`; 
      } 
    } 
   
    // 2) Now tack on “(+historical fields)” if a blue‐flag exists alongside any yellow/red: 
    if (hasHistoricalHighFidelityFieldsWarning && toolTipText !== '') { 
      toolTipText += ' (w/Historical Fields)'; 
    } 
   
    // 3) If nothing else exists but blue is true, return “Historical fields available”: 
    if (!hasHistoricalNoBidReason &&  
        !hasHistoricalDoNotBuyWarning &&  
        hasHistoricalHighFidelityFieldsWarning)  
    { 
      return '- Historical fields available'; 
    } 
   
    return toolTipText; 
  }

  const getSfhaClass = () => {
    if (opportunitySfhaDetails && opportunitySfhaDetails.isInSpecialFloodHazardArea !== undefined) {
      if (opportunitySfhaDetails.isInSpecialFloodHazardArea) {
        return classes.shfaHeaderButton;
      } else {
        return classes.headerButton;
      }
    } else {
      return classes.shfaHeaderErrorButton;
    }
  }

  const handleScrollToHistory = () => {
    if (document.getElementById('propUWHistorySection')) {
      document.getElementById('propUWHistorySection').scrollIntoView({ behavior: 'smooth' });
      setSelectedOpportunityHistoryTab('opportunities');
    }
  }

  const hasSomeWarning = titleColorClass !== null;
  return (
    <Paper className={classNames(containerClass, classes.root)} elevation={0}>
      <div className={classes.mainInfoContainer}>
        <HeaderImage
          viewOppOnChannelWorkflow={viewOppOnChannelWorkflow}
          hasInspection={hasInspection}
          inspectionClick={inspectionClick}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <div className={classNames(classes.infoSection, classes.addressSection)} style={{ padding: 0 }}>
            <div className={classes.addressSectionRow}>
              {isAmherstManaged &&
                <div style={{ display: 'flex', paddingBotton: '3px' }}>
                  <Tooltip title={"Amherst managed property"} placement="top">
                    <img src={AmherstIcon} alt="Amherst managed property" className={classes.amherstIcon} />
                  </Tooltip>
                  <Typography variant="h6" noWrap>{fullAddress(selectedOpportunity)}</Typography>
                </div>
              }
              {!isAmherstManaged && <Typography variant="h6" noWrap>{fullAddress(selectedOpportunity)}</Typography>}
            </div>
            <div className={classNames(classes.addressSectionRow, selectedContainer?.viewingReadonlyMode && classes.grayBackground)}>
              <BadgeSection selectedOpportunity={selectedContainer} />
            </div>
          </div>
          <div className={classNames(classes.vertDividerContainer, selectedContainer?.viewingReadonlyMode && classes.grayBackground)}>
            <Divider className={classes.vertDivider} />
          </div>
          <div className={classes.infoOverflow}>
            <div className={classNames(classes.infoSection, classes.priceSection, selectedContainer?.viewingReadonlyMode && classes.grayBackground)}>
              <div className={classes.textStrip}>
                <PriceSection module={Constants.Module.UNDERWRITE} selectedContainer={selectedContainer}/>
              </div>
              <div className={classes.textStrip}>
                <AuxInfo
                  amherstEstimatedAMI={selectedOpportunity?.amherstEstimatedAMI}
                  schoolScoreAverage={selectedContainer?.schoolScoreAverage}
                  schoolDistrict={selectedOpportunity?.schoolDistrict}
                  selectedContainer={selectedContainer}
                />
              </div>
            </div>
            <div className={classNames(classes.vertDividerContainer, selectedContainer?.viewingReadonlyMode && classes.grayBackground)}>
              <Divider className={classes.vertDivider} />
            </div>   
            {hasSomeWarning &&
              <div className={classes.buttonWrapper}> 
                <div className={classes.wrapper}> 
                  <Tooltip title={`Property History${getHistoryTooltip()}`} placement="bottom"> 
                    <IconButton 
                      className={classNames( 
                        classes.headerButton, titleColorClass)} 
                      aria-label="View History" 
                      onClick={handleScrollToHistory} 
                    > 
                      
                      <WarningIcon 
                        fontSize="inherit" 
                        className={classNames((titleColorClass === classes.errorButtonColor || titleColorClass === classes.blueButtonColor) && classes.whiteFill)} // White fill if red or blue button color
                      /> 
                    </IconButton> 
                  </Tooltip> 
                </div> 
              </div>
            }
            {hasCrmRelationship &&
              <div className={classes.buttonWrapper}>
                <div className={classes.wrapper}>
                  <Tooltip title="View CRM Info" placement="bottom">
                    <IconButton className={classes.headerButton} aria-label="View CRM Info" onClick={handleViewCrmInfo}>
                      <FilterDramaIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            }
            <div className={classes.buttonWrapper}>
              <div className={classes.wrapper}>
                <Tooltip title="View MSR Homes" placement="bottom">
                  <IconButton className={classes.headerButton} aria-label="View MSR Homes" onClick={() => {
                    setIsFetchingMsrHomes(true);
                    fetchMsrHomes(selectedOpportunity.id);
                    setIsMsrHomesModalOpen(true);
                  }}>
                    <HouseIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
            <div className={classes.buttonWrapper}>
              <div className={classes.wrapper}>
                <Tooltip title="View Reload Status" aria-label="View Reload Status" >
                  {selectedOpportunityReloadStatusIsLoading
                    ? <div style={{ display: 'flex', justifyContent: 'center', width: 40, height: 16 }} ><CircularProgress size={16} /></div>
                    : <div>
                        <IconButton className={classNames(classes.headerButton, reloadStatusColorClass )} aria-label="View Reload Status" onClick={handleViewReloadStatus} disabled={selectedContainer?.viewingReadonlyMode}>
                          <SvgIcon
                            className={classNames(reloadStatusColorClass === classes.errorButtonColor && !selectedContainer?.viewingReadonlyMode && classes.whiteFill)} // White fill if red button color and not disabled
                          >
                            <path d="M19,12V13.5A4,4 0 0,1 23,17.5C23,18.32 22.75,19.08 22.33,19.71L21.24,18.62C21.41,18.28 21.5,17.9 21.5,17.5A2.5,2.5 0 0,0 19,15V16.5L16.75,14.25L19,12M19,23V21.5A4,4 0 0,1 15,17.5C15,16.68 15.25,15.92 15.67,15.29L16.76,16.38C16.59,16.72 16.5,17.1 16.5,17.5A2.5,2.5 0 0,0 19,20V18.5L21.25,20.75L19,23M12,3C16.42,3 20,4.79 20,7C20,9.21 16.42,11 12,11C7.58,11 4,9.21 4,7C4,4.79 7.58,3 12,3M4,9C4,11.21 7.58,13 12,13C13.11,13 14.17,12.89 15.14,12.68C14.19,13.54 13.5,14.67 13.18,15.96L12,16C7.58,16 4,14.21 4,12V9M20,9V11H19.5L18.9,11.03C19.6,10.43 20,9.74 20,9M4,14C4,16.21 7.58,18 12,18L13,17.97C13.09,19.03 13.42,20 13.95,20.88L12,21C7.58,21 4,19.21 4,17V14Z" />
                          </SvgIcon>
                        </IconButton>
                      </div>
                  }
                </Tooltip>
              </div>
            </div>
            <div className={classes.buttonWrapper}>
              <div className={classes.wrapper}>
                <Tooltip title="View Opportunity Event History" aria-label="View Opportunity Event History" >
                  <div>
                    <IconButton className={classes.headerButton} aria-label="View Opportunity Event History" onClick={handleViewOpportunityHistory}>
                      <SvgIcon>
                        <path d="M19 19H5V8H19M16 1V3H8V1H6V3H5C3.9 3 3 3.9 3 5V19C3 20.11 3.9 21 5 21H19C20.11 21 21 20.11 21 19V5C21 3.9 20.11 3 19 3H18V1M10.88 12H7.27L10.19 14.11L9.08 17.56L12 15.43L14.92 17.56L13.8 14.12L16.72 12H13.12L12 8.56L10.88 12Z" />
                      </SvgIcon>
                    </IconButton>
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className={classes.buttonWrapper}>
              <div className={classes.wrapper}>
                <Tooltip title={isOpportunitySfhaDetailLoading ? 'SFHA' : opportunitySfhaDetails.sourceText ? `${opportunitySfhaDetails.sourceText}` : 'SFHA Error'} placement="bottom">
                  <IconButton className={getSfhaClass()} aria-label="SFHA" 
                    disabled={isOpportunitySfhaDetailLoading}
                    onClick={() => { 
                      if (!opportunitySfhaDetails.longitude || !opportunitySfhaDetails.latitude) {
                        return false;
                      }
                      window.open(`https://msc.fema.gov/portal/search?AddressQuery=${opportunitySfhaDetails.longitude},${opportunitySfhaDetails.latitude}`, '_blank');                 
                  }}>
                    <WaveIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        {displayMode === Constants.DisplayMode.DESKTOP &&
          <>
            {isAmherstManaged && 
              <UnderwriteWarningBanner
                style={{ backgroundColor: '#f8d7da', borderColor: '#f5c6cb', textAlign: 'center' }}
                message='Caution: This property might be Amherst-managed.'
                height={62}
                width={250}
              />
            }
            {selectedContainer != null && !selectedContainer.viewingReadonlyMode && <PropertyUWDiscrepantData header={true}/>}
          </>
        }
        <ViewCrmModal
          open={viewCrmModalIsOpen}
          handleClose={() => setViewCrmModalIsOpen(false)}
          txnOppId={selectedOpportunity.id}
        />
        <ViewContainersModal module={Constants.Module.UNDERWRITE} txnOppId={selectedOpportunity.id}/>
        <MsrHomesModal
          module={Constants.Module.UNDERWRITE}
          isOpen={isMsrHomesModalOpen}
          setIsOpen={setIsMsrHomesModalOpen}
        />
        <ViewOpportunityHistoryModal
          txnOppId={selectedOpportunity.id}
        />
        <ReloadStatusModal
          open={reloadStatusModalIsOpen}
          handleClose={() => setReloadStatusModalIsOpen(false)}
          txnOppId={selectedOpportunity.id}
        />
      </div>
    </Paper>
  )
};

const controlWidth = 44;
const styles = theme => ({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 0,
    borderBottom: '1px solid #ddd'
  },
  vertDividerContainer: {
    padding: `${theme.spacing(1.5)}px 0`
  },
  vertDivider: {
    width: 1,
    height: '100%'
  },
  mainInfoContainer: {
    display: 'flex',
    height: '100%',
    overflowX: 'auto',
    // overflowY: 'hidden'
  },
  infoSection: {
    display: 'flex',
    height: '80px',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  infoOverflow: {
    flexGrow: 1,
    display: 'flex', // B-added,
    // paddingRight: '10px',
  },
  addressSection: {
    minWidth: 440
  },
  priceSection: {
  },
  textStrip: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    height: 30
  },
  headerButton: {
    padding: 4,
    margin: 4
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  wrapper: {
    position: 'relative',
  },
  amherstIcon: {
    height: '25px',
    width: '25px',
    marginRight: '5px',
    marginTop: '3px',
    borderRadius: `20%`,
    boxShadow: `0px 0px 5px 0px rgba(255, 255, 255, 0.6)`
  },
  addressSectionRow: {
    paddingLeft: 16,
    paddingRight: 12,
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  grayBackground: {
    filter: 'grayscale(0.8) brightness(0.85) opacity(0.9)',
    backgroundColor: '#fff'
  },
  warningButtonColor: { 
    background: theme.palette.warning.main, 
    '&:hover': { 
      background: theme.palette.warning.dark, 
    } 
  }, 
  errorButtonColor: { 
    background: theme.palette.red.main, 
    '&:hover': { 
      background: theme.palette.red.dark, 
    } 
  }, 
  blueButtonColor: { 
    background: "#5baeff", 
    '&:hover': { 
      background: "#408cd5", 
    } 
  },
  shfaHeaderButton: {
    color: theme.palette.warning.text,
    padding: 4,
    margin: 4
  },
  shfaHeaderErrorButton: {
    color: theme.palette.red.main,
    padding: 4,
    margin: 4
  },
  whiteFill: { 
    fill: '#fff' 
  }
});

const mapStateToProps = (state) => {
  return {
    displayMode: state.appState.displayMode,
    selectedOpportunity: state.propertyUWState.propertyUWClientTxnOpp,
    selectedContainer: state.propertyUWState.selectedContainer,
    valuationIsDirty: state.propertyUWState.valuationIsDirty,
    isFetchingMsrHomes: state.propertyUWState.isFetchingMsrHomes,
    isAmherstManaged: state.propertyUWState.selectedOpportunityAmherstIndicative?.isAmherstManaged,  
    opportunitySfhaDetails: state.propertyUWState.opportunitySfhaDetails,
    isOpportunitySfhaDetailLoading: state.propertyUWState.isOpportunitySfhaDetailLoading,
    selectedOpportunityReloadStatusIsLoading: state.propertyUWState.selectedOpportunityReloadStatusState.isLoading,
    selectedOpportunityReloadStatusError: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0].error,
    selectedOpportunityReloadStatusMessage: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0].message,
    selectedOpportunityReloadStatusData: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0].categories,
    selectedOpportunityReloadStatusTopLevelStatus: state.propertyUWState.selectedOpportunityReloadStatusState.data?.[0].status,
  }
};

const mapDispatchToProps = {
  push,
  setChannelWorkflowGridStateUpdate,
  fetchMsrHomes,
  setIsFetchingMsrHomes,
  fetchOpportunityHistory,
  setSelectedOpportunityHistoryTab
};

export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(PropertyUWHeader);
